import React, { useContext, useState, useEffect, MouseEvent } from 'react';
import { useApolloClient } from '@apollo/client';
import { IconPlus, IconChevronsLeft, IconChevronsRight } from '@tabler/icons-react';
import { User } from 'lib/User';
import { Flex } from 'deprecated-enkel';
import styled from 'styled-components';

import { ARCHIVED } from 'constants/states-app';
import { UserContext } from 'constants/user-context';

import { ModalContext } from 'contexts/ModalContext';

import { AccountMenu } from 'wrappers/AccountMenu';

import { OutsideClickTracker } from 'components/OutsideClickTracker';

import { Tooltip } from 'styles';
import {
    TotemIconContainer,
    TotemsIconsContainer,
    ActiveMark,
    NewTotemIcon,
    CloseIcon,
    TotemNamePopup,
    TotemContent,
    TotemName,
    NavBarHeader,
    NavbarStyle,
    TotemIconItem,
} from 'styles/wrappers/navbar.styles';
import { RemoveIcon } from 'styles/components/card.styles';
import { PopupContent, CloseContainer, PopupTitle, Popup } from 'styles/pages/settings.styles';
import { EmojiLabel } from 'components/EmojiLabel';

import { TotemButton } from 'ui/Button';

import { HandleNotification } from 'utils';

import { totemDeletedCacheHandler } from 'utils/cache/totemDeletedCacheHandler';

import { analyticsEvents, track } from 'tracking/segment';
import { useNavigate } from 'react-router-dom';
import { TOTEM_ICONS } from 'constants/totemIcons';
import { createOptimizedImageUri } from 'utils/image/createOptimizedImageUri';
import { buildImgUrl } from 'utils/image/buildImgUrl';
import { CreateTotemPopUp } from 'components/CreateTotemPopUp';
import { useDiscardNewTotemMutation, useGetTotemsQuery } from 'data/__generated__';

function getInitialIsOpenWithLocalStorage() {
    try {
        const localStorageIsOpen = window.localStorage.getItem('is-nav-bar-open');
        return localStorageIsOpen ? JSON.parse(localStorageIsOpen) : false;
    } catch {
        return false;
    }
}

export const Navbar: React.FC = () => {
    const apolloClient = useApolloClient();
    const { organization } = useContext(UserContext);
    const { setIsModalOpen, setModal } = useContext(ModalContext);
    const isGlobalAdmin = User.isGlobalAdmin();
    const [popupOpen, setPopupOpen] = useState<boolean>(false);
    const [, setOverlayOpen] = useState<boolean>(false);
    const [hoveredTotem, setHoveredTotem] = useState<string | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [discardNewTotem] = useDiscardNewTotemMutation();

    const [isOpen, setIsOpen] = useState<boolean>(getInitialIsOpenWithLocalStorage());

    const navigate = useNavigate();

    const { loading, error, data: totemData } = useGetTotemsQuery();

    useEffect(() => {
        window.localStorage.setItem('is-nav-bar-open', JSON.stringify(isOpen));
    }, [isOpen]);

    function updateIsOpen(isOpen: boolean) {
        setIsOpen(isOpen);
    }

    const handleTotemHover = (totemId: string, event: MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setPopupPosition({
            top: rect.top + rect.height / 2,
            left: rect.right + 10,
        });
        setHoveredTotem(totemId);
    };

    const handleTotemLeave = () => {
        setHoveredTotem(null);
    };

    if (error) {
        throw error;
    }

    if (loading) {
        return null;
    }

    const { totemsOfUser: totems } = totemData ?? { totemsOfUser: [] };

    const handleDiscardTotem = async (totemId: string, number: number) => {
        try {
            if (totems?.length !== number) {
                setPopupOpen(true);
            } else {
                track(analyticsEvents.DISCARD_TOTEM);
                await discardNewTotem({ variables: { totemId } });
                totemDeletedCacheHandler({
                    apolloClient,
                    deletedTotemId: totemId,
                });
                navigate('/');
            }
        } catch (error: any) {
            HandleNotification(error);
        }
    };

    function isItemActive(uri: string, homepage?: boolean) {
        return homepage ? window.location.pathname === uri : window.location.pathname.includes(uri);
    }

    return (
        <NavbarStyle $isOpen={isOpen}>
            <NavBarHeader $isOpen={isOpen}>
                {isOpen ? (
                    <IconChevronsLeft onClick={() => updateIsOpen(!isOpen)} />
                ) : (
                    <IconChevronsRight onClick={() => updateIsOpen(!isOpen)} />
                )}
            </NavBarHeader>
            <TotemsScrollContainer>
                <TotemsIconsContainer $isOpen={isOpen}>
                    {totems?.map((totem) => {
                        const { _id, name, number, state, hasValidatedOrders, microstore } = totem ?? {
                            _id: '',
                            name: '',
                            number: 0,
                            state: '',
                            hasValidatedOrders: false,
                            microstore: false,
                        };
                        // Ensure number is a valid number and index is within bounds
                        let iconIndex: number =
                            typeof number === 'number' && !isNaN(number) ? (number - 1) % TOTEM_ICONS.length : 0;
                        if (iconIndex < 0 || iconIndex >= TOTEM_ICONS.length) iconIndex = 0;
                        const totemCard = `img/illustrations/Totem_illustration-${TOTEM_ICONS[iconIndex].id}.png`;
                        const showTotem = User.canAccessTotem(_id ?? '');
                        return (
                            showTotem && (
                                <TotemIconContainer key={_id}>
                                    {state === ARCHIVED && (
                                        <EmojiLabel src="🔴" size="14px" position={{ top: 0, right: '6px' }} />
                                    )}
                                    <TotemContent>
                                        <TotemIconItem
                                            to={`/totem/${_id}`}
                                            color={TOTEM_ICONS[iconIndex].color}
                                            data-for="totemTooltip"
                                            data-tip={name || `TOTEM #${number ?? ''}`}
                                            $imgsrc={createOptimizedImageUri({ src: buildImgUrl(totemCard) })}
                                            $active={isItemActive(`/totem/${_id}`)}
                                            data-test="totem-icon"
                                            onClick={() => track(analyticsEvents.CLICK_TOTEM_ICON)}
                                            $imageSize="65% 65%"
                                            onMouseEnter={(event: MouseEvent<HTMLElement>) =>
                                                !isOpen && handleTotemHover(_id, event)
                                            }
                                            onMouseLeave={handleTotemLeave}
                                        />
                                        <TotemName $active={isItemActive(`/totem/${_id}`)}>{name}</TotemName>
                                    </TotemContent>
                                    {!isOpen && <Tooltip id="totemTooltip" type="dark" effect="solid" place="right" />}
                                    <ActiveMark $active={isItemActive(`/totem/${_id}`)} />
                                    {!isOpen && (
                                        <TotemNamePopup
                                            className="totem-popup"
                                            $show={hoveredTotem === _id}
                                            $top={popupPosition.top}
                                            $left={popupPosition.left}
                                        >
                                            {name}
                                        </TotemNamePopup>
                                    )}
                                    {!hasValidatedOrders && !microstore && number !== 1 ? (
                                        <>
                                            <CloseIcon
                                                size="15"
                                                onClick={() => handleDiscardTotem(_id, number)}
                                                $active={isItemActive(`/totem/${_id}`)}
                                            />
                                            <NewTotemIcon size="17" $active={isItemActive(`/totem/${_id}`)} />
                                        </>
                                    ) : null}
                                </TotemIconContainer>
                            )
                        );
                    })}
                </TotemsIconsContainer>
            </TotemsScrollContainer>

            <BottomActionsContainer>
                {isGlobalAdmin && (
                    <IconContainer>
                        <IconButtonItem
                            data-for="createTotemTooltip"
                            data-tip="Créer un autre point de livraison"
                            data-test="add-totem"
                            onClick={() => {
                                setIsModalOpen(true);
                                setModal(<CreateTotemPopUp setIsModalOpen={setIsModalOpen} />);
                                track(analyticsEvents.START_TOTEM_CREATION);
                            }}
                            type="secondary"
                        >
                            <IconPlus />
                        </IconButtonItem>
                        <Tooltip id="createTotemTooltip" type="dark" effect="solid" place="right" />
                    </IconContainer>
                )}

                <IconContainer>
                    <OutsideClickTracker
                        setOverlayOpen={setOverlayOpen}
                        render={(open, setOpen, setOverlayOpen) => (
                            <AccountMenu
                                open={open}
                                setOpen={setOpen}
                                setOverlayOpen={setOverlayOpen}
                                imageUrl={organization?.imageUrl ?? ''}
                            />
                        )}
                    />
                </IconContainer>
            </BottomActionsContainer>

            {popupOpen && (
                <Popup $alignItems="center" $justifyContent="center" onClick={() => setPopupOpen(false)}>
                    <PopupContent
                        $paddings={[2, 2, 4, 2]}
                        $spaceBetween
                        $width="520px"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PopupTitle>Suppression de TOTEM</PopupTitle>
                        <Flex $direction="column">
                            <InfoText>
                                Pour supprimer ce TOTEM, vous devez d'abord supprimer le TOTEM n°{totems?.length}
                            </InfoText>
                            <TotemButton onClick={() => setPopupOpen(false)}>J'ai compris</TotemButton>
                        </Flex>
                        <CloseContainer>
                            <RemoveIcon size="25" onClick={() => setPopupOpen(false)} />
                        </CloseContainer>
                    </PopupContent>
                </Popup>
            )}
        </NavbarStyle>
    );
};

const InfoText = styled.p`
    margin-bottom: 30px;
`;

const TotemsScrollContainer = styled.div`
    position: relative;
    display: flex;
    flex-flow: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const BottomActionsContainer = styled.div`
    position: relative;
    display: flex;
    flex-flow: column;
    min-height: auto;
`;

interface IconContainerProps {
    height?: number;
}
export const IconContainer = styled.div<IconContainerProps>`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    height: ${({ height }) => (height ? height : 75)}px;
`;

interface IconButtonItemProps {
    type?: 'secondary' | string;
}
const IconButtonItem = styled.button<IconButtonItemProps>`
    cursor: pointer;
    position: relative;
    transition: all 0.25s ease;
    color: ${(props) => (props.type === 'secondary' ? props.theme.colors.pantinGrey : props.theme.colors.black)};
    border: 2px solid ${(props) => (props.type === 'secondary' ? 'transparent' : props.theme.colors.greenDarker)};
    background: ${(props) => (props.type === 'secondary' ? 'transparent' : props.theme.colors.green)};
    &:hover {
        background: ${(props) =>
            props.type === 'secondary' ? props.theme.colors.darkGrey : props.theme.colors.greenDarker};
    }
    border-radius: ${({ theme }) => theme.variables.borderRadius};
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
`;
